import { Injectable } from '@angular/core';
import { ConfigService } from './config.service';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as _ from 'lodash';
import { DiagnosisVo2, MedicineInfo } from './api-client.service';
import MeiliSearch, { Hits, Index } from 'meilisearch';
import { instantMeiliSearch, InstantMeiliSearchInstance } from '@meilisearch/instant-meilisearch';
import { environment } from '../../../environments/environment';

export interface MedicineCategory {
  hbATC2Code: string;
  hbDrugClassGroupDescription: string;

  hbATC3Code: string;
  hbDrugClassDescription: string;
}

interface SearchResult {
  Code: string;
  Description: string;
  Parameters: any;
}

@Injectable({
  providedIn: 'root',
})
export class MeiliSearchRestService {
  private medicineIndex: Index;
  private diagnosisIndex: Index;
  public tariffCodeIndex: Index;

  public meiliSearchLookupClient!: MeiliSearch;
  public meiliSearchLookupDataClient!: MeiliSearch;
  public meiliInstantSearchClient: InstantMeiliSearchInstance;

  constructor(private configService: ConfigService) {
    this.meiliSearchLookupClient = new MeiliSearch({
      host: environment.meiliSearchLookup.host,
      apiKey: environment.meiliSearchLookup.key,
    });

    this.meiliSearchLookupDataClient = new MeiliSearch({
      host: environment.meiliSearchLookupData.host,
      apiKey: environment.meiliSearchLookupData.key,
    });

    this.diagnosisIndex = this.meiliSearchLookupDataClient.index(environment.meiliSearchLookupData.diagnosisIndexName);
    this.medicineIndex = this.meiliSearchLookupDataClient.index(environment.meiliSearchLookupData.medicineIndexName);
    this.tariffCodeIndex = this.meiliSearchLookupClient.index(environment.meiliSearchAccount.tariffCodeIndexName);

    this.meiliInstantSearchClient = instantMeiliSearch(environment.meiliSearchLookupData.host, environment.meiliSearchLookupData.key);
  }

  getMeiliDiagnosisToken(diagnosisCode: string): Observable<string> {
    return from(
      this.diagnosisIndex.search<DiagnosisVo2>(diagnosisCode, {
        limit: 1,
      })
    ).pipe(
      map(t => t.hits as any),
      map(records => records.map(record => (record.DEPToken as string) || null)),
      map(records => _.head(records) as string)
    );
  }

  async getDiagnosis(diagnosisCode: string) {
    return this.diagnosisIndex
      .search(diagnosisCode, {
        limit: 1,
      })
      .then(t => t.hits)
      .then(records => _.head(records));
  }

  getMeiliMedicineCategories() {
    return from(
      this.medicineIndex.search('', {
        limit: 1000,
        attributesToRetrieve: ['hbATC2Code', 'hbATC3Code', 'hbDrugClassDescription', 'hbDrugClassGroupDescription'],
      })
    ).pipe(
      map(records =>
        records.hits.map(
          record =>
            ({
              hbATC2Code: record.hbATC2Code,
              hbATC3Code: record.hbATC3Code,
              hbDrugClassDescription: record.hbDrugClassDescription,
              hbDrugClassGroupDescription: record.hbDrugClassGroupDescription,
            }) as MedicineCategory
        )
      ),
      map(records => _.sortBy(records, record => [record.hbDrugClassGroupDescription, record.hbDrugClassDescription]))
    );
  }

  /**
   * Support lookup for nappi codes with 3 forms (10-digit including pack size, 7-digit with leading zero, 6-digit)
   */
  getMedicineInformationByNappi(nappiCode: string) {
    return from(this.medicineIndex.search(nappiCode)).pipe(
      map(r => r.hits.map(h => h.data)),
      map(lines =>
        lines.find(
          line =>
            line.PackInfo.map(m => parseInt(m.NAPPICode10)).includes(parseInt(nappiCode)) ||
            line.NAPPICode === nappiCode ||
            line.NAPPICode7 === nappiCode
        )
      )
    );
  }

  getMedicineInfosByFamilyAndForm(medicinefamily: string) {
    return from(
      this.medicineIndex.search<MedicineInfo>('', {
        filter: `(hbDrugFamilyGroup = "${medicinefamily}" OR hbDrugFamilyName = "${medicinefamily}")`,
      })
    ).pipe(map(r => r.hits));
  }

  searchTariffCodeIndex(lookupfilter: string, type: string): Observable<Hits<SearchResult>> {
    const index = this.tariffCodeIndex;
    const filter = ['TypeCode=10', `TariffMedicalTypeCode=${type}`];

    const res = index.search<SearchResult>(lookupfilter, {
      filter,
      limit: 10,
    });

    return from(res).pipe(
      // todo once confirmed uncomment #36722
      map(s => s.hits?.filter(hit => !hit.Code.endsWith('A')))
    );
  }

  getTariffCode(type: string, code: string): Promise<Hits<SearchResult>> {
    const index = this.tariffCodeIndex;
    const filter = ['TypeCode=10', `TariffMedicalTypeCode=${type}`, `Code=${code}`];

    const res = index.search<SearchResult>('', {
      filter,
      limit: 10,
    });

    return res.then(s => s.hits);
  }

  searchModifierCodeIndex(lookupfilter: string, type: string): Observable<Hits<SearchResult>> {
    const index = this.tariffCodeIndex;
    const filter = ['TypeCode=99', `TariffMedicalTypeCode=${type}`];

    const res = index.search<SearchResult>(lookupfilter, {
      filter,
      limit: 10,
    });

    return from(res).pipe(map(s => s.hits));
  }
}
